import { useEffect, useRef, useState } from 'react';
import { Check, CopyIcon, Gift } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getReferralLink } from '../../../../auth/_requests';
import { useAuth } from '../../../../auth/Auth';
import { useModal } from '../../../hooks/modalContext';
import { useReferralReward } from '../../../hooks/useReferralReward';

const REFERRAL_HEADLINE = 'Know someone who needs TenantPay?';

export const ReferralPrompt = () => {
  const { auth } = useAuth();
  const { summaryText } = useReferralReward();
  const referralMessage = summaryText
    ? `Share your link and earn ${summaryText} when they sign up and pay.`
    : 'Share your link and earn rewards when they sign up.';
  const { setModal } = useModal();
  const navigate = useNavigate();
  const [link, setLink] = useState('');
  const [copied, setCopied] = useState(false);
  const copiedTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    let cancelled = false;
    const fetchLink = async () => {
      try {
        const response = await getReferralLink(auth?.data?.token);
        if (!cancelled) setLink(response);
      } catch {
        // Referral link is a bonus surface here — fail silently rather than
        // disrupting the payment success screen.
      }
    };
    fetchLink();
    return () => {
      cancelled = true;
    };
  }, [auth?.data?.token]);

  useEffect(() => {
    return () => clearTimeout(copiedTimeoutRef.current);
  }, []);

  const handleCopy = async () => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      clearTimeout(copiedTimeoutRef.current);
      copiedTimeoutRef.current = setTimeout(() => setCopied(false), 3000);
    } catch {
      // ignore
    }
  };

  if (!link) return null;

  return (
    <div className='flex flex-col px-6 py-6 items-start justify-center gap-3 rounded-[20px] border border-[#E9EDF9] bg-[linear-gradient(229deg,_#FFF_-13.57%,_#F2F4F9_113.63%)] w-full'>
      <div className='flex items-center gap-2'>
        <Gift className='text-primary h-5 w-5' />
        <span className='text-main text-base font-semibold'>{REFERRAL_HEADLINE}</span>
      </div>
      <span className='text-main text-sm'>{referralMessage}</span>
      <div className='inputField w-full justify-between flex'>
        <span className='truncate max-w-[calc(100%-40px)] overflow-hidden whitespace-nowrap'>
          {link}
        </span>
        <button type='button' onClick={handleCopy} aria-label='Copy referral link'>
          {copied ? (
            <Check stroke='var(--primary-color)' className='h-5 w-5' />
          ) : (
            <CopyIcon stroke='var(--primary-color)' className='h-5 w-5' />
          )}
        </button>
      </div>
      <button
        type='button'
        className='text-primary text-sm font-semibold self-start hover:underline'
        onClick={() => {
          setModal(null);
          navigate('/refer');
        }}
      >
        View referral rewards →
      </button>
    </div>
  );
};
